import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Globe, Check, Quote, BookOpen, Brain, MessageSquare, TrendingUp, AlertCircle, Mail, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { trackSignUp } from '../../utils/ga';

export default function SignUpPage() {
  const navigate = useNavigate();
  const authContext = useAuth();
  
  const { 
    signUp = null,
    signInWithGoogle = null,
    loading = true, 
    authReady = false,
    user = null 
  } = authContext || {};
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [verificationSent, setVerificationSent] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const languages = ['English', 'Spanish', 'French', 'German', 'Portuguese'];

  useEffect(() => {
    if (user && !loading) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const isAuthServiceAvailable = () => {
    return authContext && signUp && typeof signUp === 'function' && authReady;
  };

  const validateForm = () => {
    if (!email?.trim()) {
      setError('Please enter your email address.');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(email)) {
      setError('Please enter a valid email address.');
      return false;
    }
    if (!password?.trim()) {
      setError('Please enter a password.');
      return false;
    }
    if (password?.length < 6) {
      setError('Password must be at least 6 characters long.');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }
    return true;
  };

  const sendVerificationEmail = async (emailAddr) => {
    try {
      const { error } = await supabase?.functions?.invoke('send-verification-email', {
        body: { email: emailAddr },
      });
      if (error) {
        console.warn('Verification email send failed:', error?.message);
      }
    } catch (err) {
      console.warn('Verification email error:', err?.message);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    if (authContext?.networkError) {
      setError('Network connection issue detected. Please check your internet connection and try again.');
      return;
    }

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const { data, error: signUpError } = await signUp(email, password);
      
      if (signUpError) {
        if (signUpError?.message?.includes('Network') || signUpError?.message?.includes('fetch')) {
          setError('Unable to connect to the server. Please check your internet connection and try again.');
        } else if (signUpError?.message?.includes('timeout')) {
          setError('Connection timeout. Please try again.');
        } else if (signUpError?.message?.includes('already registered') || signUpError?.message?.includes('User already registered')) {
          setError('This email is already registered. Please sign in or use a different email.');
        } else {
          setError(signUpError?.message || 'Failed to create account');
        }
        return;
      }

      // Check if email confirmation is required (user not immediately confirmed)
      const needsConfirmation = data?.user && !data?.user?.email_confirmed_at;
      
      if (needsConfirmation) {
        // Supabase already sends a confirmation email automatically.
        // We do NOT call sendVerificationEmail here to avoid sending duplicate emails
        // and to prevent token conflicts from generateLink creating a second token.
        setVerificationSent(true);
        setResendCooldown(60);
      } else {
        setSuccess('Account created successfully! Redirecting to dashboard...');
        trackSignUp('email');
        setTimeout(() => navigate('/dashboard'), 1500);
      }
    } catch (err) {
      console.error('Sign up error:', err);
      setError(err?.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    try {
      // Use only the edge function for resend — it generates a fresh branded link.
      // Do NOT also call supabase.auth.resend here as that creates two competing tokens.
      const { error: edgeError } = await supabase?.functions?.invoke('send-verification-email', {
        body: { email: email },
      });
      if (edgeError) {
        // Fallback to Supabase built-in resend if edge function fails
        await supabase?.auth?.resend({
          type: 'signup',
          email: email,
        });
      }
      setResendCooldown(60);
    } catch (err) {
      console.warn('Resend failed:', err?.message);
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      const { error } = await signInWithGoogle?.();
      if (error) {
        setError(error?.message || 'Google sign-up failed. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred with Google sign-up.');
    } finally {
      setGoogleLoading(false);
    }
  };

  if (!authContext || (!authReady && loading)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Initializing authentication service...</p>
          <p className="text-muted-foreground text-sm mt-2">Please wait while we prepare your signup experience</p>
        </div>
      </div>
    );
  }

  if (authReady && !signUp) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center bg-card rounded-lg shadow-lg p-8 max-w-md mx-4 border border-border">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-foreground mb-2">Authentication Service Error</h1>
          <p className="text-muted-foreground mb-4">Unable to initialize the authentication service.</p>
          <button onClick={() => window.location?.reload()} className="bg-destructive text-destructive-foreground px-6 py-2 rounded-lg hover:opacity-90 transition-opacity">
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  // Email verification sent screen
  if (verificationSent) {
    return (
      <div className="min-h-screen flex items-center justify-center relative py-12 px-4 sm:px-6 lg:px-8">
        <div 
          className="fixed inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/assets/images/FortifiedMindSplash1-1769379659227.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            filter: 'brightness(0.7)'
          }}
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 w-full max-w-lg mx-auto">
          <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl p-8 text-center border border-white/30">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Check Your Email</h1>
            <p className="text-gray-600 mb-2 text-base leading-relaxed">
              We sent a verification link to:
            </p>
            <p className="text-primary font-semibold text-lg mb-6 break-all">{email}</p>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 text-left">
              <p className="text-blue-800 text-sm font-semibold mb-2">📋 Next Steps:</p>
              <ol className="text-blue-700 text-sm space-y-1 list-decimal list-inside">
                <li>Open your email inbox</li>
                <li>Click the "Verify My Email" button in the email</li>
                <li>You will be redirected to your dashboard</li>
              </ol>
            </div>
            <p className="text-gray-500 text-sm mb-6">
              Didn't receive the email? Check your spam folder or resend below.
            </p>
            <button
              onClick={handleResendVerification}
              disabled={resendCooldown > 0 || resendLoading}
              className="w-full flex items-center justify-center space-x-2 py-3 px-6 border-2 border-primary text-primary rounded-xl font-semibold hover:bg-primary/5 transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-4"
            >
              {resendLoading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                  <span>Sending...</span>
                </>
              ) : resendCooldown > 0 ? (
                <>
                  <RefreshCw className="w-4 h-4" />
                  <span>Resend in {resendCooldown}s</span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  <span>Resend Verification Email</span>
                </>
              )}
            </button>
            <Link
              to="/login"
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Sign Up Free | Fortified Mind — Scripture Memorization & Bible Study</title>
        <meta name="description" content="Create your free Fortified Mind account. Start memorizing Bible verses with spaced repetition, access Bible reading plans, EGW writings, commentaries, and devotional tools. Open to all denominations." />
        <meta name="keywords" content="sign up scripture memorization, free Bible study app, Seventh-day Adventist tools, memorize Bible verses free, Christian app signup, Bible app account, SDA Bible tools, all denominations Bible study" />
        <link rel="canonical" href="https://fortifiedmind.org/signup" />
        <meta property="og:title" content="Sign Up Free | Fortified Mind — Scripture Memorization & Bible Study" />
        <meta property="og:description" content="Create your free account and start memorizing Bible verses with spaced repetition, reading plans, EGW writings, and more. Open to all Christian denominations." />
        <meta property="og:url" content="https://fortifiedmind.org/signup" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://fortifiedmind.org/assets/images/FortifiedMindSplash1-1769379659227.jpeg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sign Up Free | Fortified Mind — Scripture Memorization & Bible Study" />
        <meta name="twitter:description" content="Create your free account and start memorizing Bible verses with spaced repetition, reading plans, EGW writings, and more." />
        <meta name="twitter:image" content="https://fortifiedmind.org/assets/images/FortifiedMindSplash1-1769379659227.jpeg" />
      </Helmet>
      <div className="min-h-screen flex items-center justify-center relative py-12 px-4 sm:px-6 lg:px-8">
        {/* Background Image */}
        <div 
          className="fixed inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/assets/images/FortifiedMindSplash1-1769379659227.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            filter: 'brightness(0.7)'
          }}
        />
        <div className="absolute inset-0 bg-black/40" />

        <div className="relative z-10 w-full max-w-7xl mx-auto flex justify-start">
          <div className="w-full max-w-2xl space-y-8">

            {/* App Header */}
            <div className="text-center">
              <div className="mx-auto h-20 w-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center mb-6 shadow-2xl">
                <svg className="w-10 h-10 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h1 className="text-5xl font-bold text-white mb-4 tracking-tight drop-shadow-2xl">FORTIFIED MIND</h1>
              <p className="text-white text-xl mb-2 drop-shadow-lg font-semibold">Scripture Memorization & Reflection</p>
              <p className="text-gray-100 text-lg drop-shadow-lg">Strengthen your faith through God's Word</p>
            </div>

            {/* Ellen White Quote Section */}
            <div className="bg-primary/20 backdrop-blur-sm rounded-2xl shadow-2xl p-6 text-white border border-white/20">
              <div className="flex items-start space-x-4">
                <Quote className="w-8 h-8 text-primary/80 flex-shrink-0 mt-1" />
                <div>
                  <blockquote className="text-lg italic leading-relaxed mb-3">
                    "None but those who have <span className="font-bold text-warning bg-warning/20 px-1 rounded">fortified</span> the <span className="font-bold text-warning bg-warning/20 px-1 rounded">mind</span> with the truths of the Bible will stand through the last great conflict."
                  </blockquote>
                  <cite className="text-white/70 text-sm font-medium">— Ellen G. White</cite>
                </div>
              </div>
            </div>

            {/* Sign Up Card */}
            <div className="bg-white/85 backdrop-blur-md rounded-2xl shadow-2xl p-6 sm:p-8 space-y-5 border border-white/30">
              {/* Free Registration Emphasis */}
              <div className="text-center bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-green-800 font-semibold text-lg">🎉 Create Your Free Account</p>
                <p className="text-green-700 text-sm mt-1">No credit card required • Instant access • Always free</p>
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 flex items-center space-x-3">
                  <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-destructive font-medium">Sign Up Error</p>
                    <p className="text-destructive/80 text-sm">{error}</p>
                  </div>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="bg-success/10 border border-success/30 rounded-lg p-4 flex items-center space-x-3">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-success font-medium">Success!</p>
                    <p className="text-success/80 text-sm">{success}</p>
                  </div>
                </div>
              )}

              {/* Google Sign Up Button */}
              <button
                onClick={handleGoogleSignUp}
                disabled={googleLoading || !isAuthServiceAvailable()}
                className="w-full flex items-center justify-center space-x-3 py-3 px-4 border-2 border-gray-300 rounded-xl bg-white hover:bg-gray-50 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-gray-700 shadow-sm min-h-[52px] touch-manipulation"
              >
                {googleLoading ? (
                  <span className="flex items-center space-x-2">
                    <span className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full" />
                    <span>Signing up with Google...</span>
                  </span>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span>Continue with Google</span>
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300/60" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white/85 text-gray-500 rounded">or sign up with email</span>
                </div>
              </div>

              {/* Sign Up Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  floating
                  label="Email Address"
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  required
                  className="w-full text-base touch-manipulation"
                />

                <Input
                  floating
                  label="Password (min. 6 characters)"
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value)}
                  required
                  className="w-full text-base touch-manipulation"
                />

                <Input
                  floating
                  label="Confirm Password"
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e?.target?.value)}
                  required
                  className="w-full text-base touch-manipulation"
                />

                <Button
                  type="submit"
                  disabled={isLoading || !isAuthServiceAvailable()}
                  className="w-full bg-primary text-primary-foreground py-4 sm:py-3 rounded-xl hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 text-base font-semibold min-h-[52px] touch-manipulation"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center space-x-2">
                      <span className="animate-spin h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
                      <span>Creating Account...</span>
                    </span>
                  ) : 'Create Free Account'}
                </Button>
              </form>

              {/* Terms and Privacy */}
              <div className="text-center text-xs text-gray-500">
                <p>By creating an account, you agree to our</p>
                <div className="flex justify-center space-x-2 mt-1">
                  <Link to="/terms-of-service" className="hover:text-gray-700 underline py-1 touch-manipulation">Terms of Service</Link>
                  <span>and</span>
                  <Link to="/privacy-policy" className="hover:text-gray-700 underline py-1 touch-manipulation">Privacy Policy</Link>
                </div>
              </div>

              {/* Already Have Account Link */}
              <div className="text-center border-t border-gray-200 pt-4">
                <p className="text-sm text-gray-600">
                  Already have an account?{' '}
                  <Link to="/login" className="text-primary hover:text-primary/80 font-semibold py-1 touch-manipulation">Sign In</Link>
                </p>
              </div>

              {/* Language Selection */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-center">
                  <Globe className="h-4 w-4 text-gray-400 mr-2" />
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e?.target?.value)}
                    className="text-sm text-gray-600 bg-transparent border-none focus:outline-none cursor-pointer disabled:opacity-50 py-2 touch-manipulation"
                    disabled={isLoading || !isAuthServiceAvailable()}
                  >
                    {languages?.map((lang) => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* App Features Section */}
            <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-6 border-l-4 border-white border border-white/30">
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900">What You'll Get With Your Free Account</h2>
                <p className="text-gray-700 leading-relaxed">
                  Join thousands of believers strengthening their faith through systematic Scripture memorization and deep reflection.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-3 p-3 bg-primary/10 rounded-lg">
                    <Brain className="w-5 h-5 text-primary flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-900">Scripture Memorization System</span>
                      <p className="text-xs text-gray-600">Advanced spaced repetition techniques</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-accent/10 rounded-lg">
                    <MessageSquare className="w-5 h-5 text-accent flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-900">Reflection & Journaling</span>
                      <p className="text-xs text-gray-600">Personal insights and spiritual growth</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-success/10 rounded-lg">
                    <BookOpen className="w-5 h-5 text-success flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-900">SDA Bible Commentary</span>
                      <p className="text-xs text-gray-600">Complete commentary integration</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-warning/10 rounded-lg">
                    <Quote className="w-5 h-5 text-warning flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-900">Ellen G. White Comments</span>
                      <p className="text-xs text-gray-600">Inspired insights on every passage</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-primary/10 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-primary flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-900">Progress Tracking</span>
                      <p className="text-xs text-gray-600">Monitor memorization milestones</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-destructive/10 rounded-lg">
                    <Check className="w-5 h-5 text-destructive flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-900">Interactive Reading Plans</span>
                      <p className="text-xs text-gray-600">Structured Bible study programs</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}